import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DatasetContextType } from '../../types/context';
import { useDataset } from '../../api/hooks/useDataset';
import { useFilter } from '../../api/hooks/useFilter';
import { getColumnsWidth } from '../../utils/getColumnsWidth';
import {
  getColumnByFilterParam,
  getMultisortFromDataset,
  isEmptyValue,
  updateActiveSortingFromLazyState,
} from '../../utils/managerUtils';
import { getDefaultFilterFromApiResponse } from '../../utils/filters/getDefaultFilterFromApiResponse';
import { getSupportedDefaultMatchMode } from '../../utils/filters/getSupportedDefaultMatchMode';
import { DEFAULT_LAZY_STATE, GRID_DATA_LIMIT } from '../../constants';
import toast from 'react-hot-toast';
import { getPeersetFilterTransition } from '../../utils/getPeersetFilterTransition';
import {
  ColumnsWitdhType,
  Dataset,
  GlobalLocationStateType,
} from '../../types/types';
import { useHistory, useLocation } from 'react-router';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useFormTokenChainContext } from '../../hooks/context/useFormTokenChainContext';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';
import { useQuickBookmarkFilterRestore } from './hooks/useQuickBookmarkFilterRestore';

import { updateLazyStateFromActiveFilters } from '../DatasetFormDataContext/utils/updateLazyStateFromActiveFilters';
import { useDatasetData } from '../../api/hooks/useDatasetData';
import { useFormPollErrorGuard } from './hooks/useFormPollErrorGuard';
import { useDatasetControlData } from './hooks/useDatasetControlData';
import { parseDatasetControlData } from '../../utils/datasetControl/parseDatasetControlData';
import { updateActiveFiltersFromLazyState } from '../../utils/filters/updateActiveFiltersFromLazyState';
import {
  DatasetDataApiRequestData,
  SubsetDataApiRequestData,
} from '../../api/types/types';
import { useUserState } from '../../hooks/context/useUserState';
import { getFilterParamObject } from '../../utils/apiRequests/getFilterParamObject';
import { stripEmptyActiveFilters } from '../../utils/apiRequests/stripEmptyActiveFilters';
import { apiRequest } from '../../api/utils/apiRequest';
import { getDataParamsString } from '../../utils/apiRequests/getDataParamStrings';
import { getDatasetDataApiRequestData } from '../../api/utils/requestData/getDatasetDataApiRequestData';
import { setDatasetFetchState } from '../../utils/apiRequests/setDatasetFetchState';
import { getSubsetDataApiRequestData } from '../../api/utils/requestData/getSubsetDataApiRequestData';
import { resolveTemplate } from '../../utils/json/resolveTemplate';
import { fetchRowIds } from '../../utils/dataset/fetchRowIds';
import { buildLocateRequestData } from '../../utils/dataset/buildLocateRequestData';
import { computeCustomLocateLimit } from '../../utils/dataset/computeCustomLocateLimit';
import { probeRowIndexesByIds } from '../../utils/dataset/probeRowIndexesByIds';
import { syncCachedQueryIdRef } from '../../utils/dataset/syncCachedQueryIdRef';
import { useDatasetSessionStorageContext } from '../../hooks/context/useDatasetSessionStorageContext';
import useRoutes from '../../hooks/useRoutes';
import { getSubsetAndItemParams } from '../../utils/getSubsetAndItemParams';
import { isFromPageReload } from '../../utils/isFromPageReload';
import { getDataFromDatasetRequest } from '../../api/utils/getDataFromDatasetRequest';
import { isPeersetSwitch } from '../DatasetFormDataContext/utils/isPeersetSwitch';
import { resolveDatasetItemsVisibility } from '../../utils/dataset/resolveDatasetItemsVisibility';
import { useDatasetVisibilityResolve } from '../../hooks/useDatasetVisibilityResolve';
import { withSyntheticRowIds } from '../../utils/withSyntheticRowIds';
import { debugLogger } from '../../debug/DebugLogger';

const logFilters = debugLogger.scope('filters');
const logDatasetControl = debugLogger.scope('datasetControl');
const logLocate = debugLogger.scope('locate');

const defaultValues: DatasetContextType = {
  isProvided: false,
  activeFilters: null,
  activeSorting: [],
  apiError: undefined,
  cachedQueryIdRef: '',
  columnsWidth: undefined,
  datasetId: undefined,
  subsetId: undefined,
  data: undefined,
  locallyRemovedRowsCount: 0,
  dataset: undefined,
  datasetData: undefined,
  disabledLazyLoading: false,
  gridHeaderHeight: 0,
  horizontalScroll: 0,
  isLayoutLoading: true,
  isGridLoading: true,
  isDatasetHookLoading: false,
  dataHookLoadingDatasetId: undefined,
  lazyState: DEFAULT_LAZY_STATE,
  referenceDatasets: {},
  slaveDatasets: {},
  slaveLazyStates: {},
  slaveFilters: {},
  slaveTabIndex: 0,
  filter: null,
  peersetFilterFirstProcessed: false,
  datasetControl: null,
  clearDatasetControl: () => {
    throw new Error('clearDatasetControl must be implemented');
  },
  fetchDatasetData: () => {
    throw new Error('fetchDatasetData must be implemented');
  },
  reloadDatasetDefinition: () => {
    throw new Error('reloadDatasetDefinition must be implemented');
  },
  setActiveFilters: () => {
    throw new Error('setActiveFilters must be implemented');
  },
  setDatasetId: () => {
    throw new Error('setDatasetId must be implemented');
  },
  loadMoreData: () => {
    throw new Error('loadMoreData must be implemented');
  },
  removeRowsLocally: () => {
    throw new Error('removeRowsLocally must be implemented');
  },
  patchRowLocally: () => {
    throw new Error('patchRowLocally must be implemented');
  },
  wasRowJustRemoved: () => {
    throw new Error('wasRowJustRemoved must be implemented');
  },
  locateAndFetchById: () => {
    throw new Error('locateAndFetchById must be implemented');
  },
  locateAndFetchFarthestByIds: () => {
    throw new Error('locateAndFetchFarthestByIds must be implemented');
  },
  setLazyState: () => {
    throw new Error('setLazyState must be implemented');
  },
  setGridHeaderHeight: () => {
    throw new Error('setGridHeaderHeight must be implemented');
  },
  setReferenceDatasets: () => {
    throw new Error('setReferenceDatasets must be implemented');
  },
  setHorizontalScroll: () => {
    throw new Error('setHorizontalScroll must be implemented');
  },
  setSlaveDatasets: () => {
    throw new Error('setSlaveDatasets must be implemented');
  },
  setSlaveLazyStates: () => {
    throw new Error('setSlaveLazyStates must be implemented');
  },
  setSlaveFilters: () => {
    throw new Error('setSlaveFilters must be implemented');
  },
  setSlaveTabIndex: () => {
    throw new Error('setSlaveTabIndex must be implemented');
  },
  setSubsetId: () => {
    throw new Error('setSubsetId must be implemented');
  },
  setPeersetFilterFirstProcessed: () => {
    throw new Error('setFilterFirstProcessed must be implemented');
  },
  resetDatasetSetup: () => {
    throw new Error('resetDatasetSetup must be implemented');
  },
};

export const DatasetContext = createContext<DatasetContextType>(defaultValues);

const DatasetContextProvider = ({ children }: { children: ReactNode }) => {
  // LAZY STATE
  const [lazyState, setLazyState] = useState<Record<string, unknown>>(
    defaultValues.lazyState
  );
  const [slaveLazyStates, setSlaveLazyStates] = useState<
    Record<string, unknown>
  >(defaultValues.slaveLazyStates);
  const [lazyLoading, setLazyLoading] = useState(false);
  const [disabledLazyLoading, setDisabledLazyLoading] = useState(
    defaultValues.disabledLazyLoading
  );
  // DATASET
  const [datasetId, setDatasetId] = useState<string | undefined>(
    defaultValues.datasetId
  );
  const [subsetId, setSubsetId] = useState<string | undefined>(
    defaultValues.subsetId
  );
  const [datasetLoading, setDatasetLoading] = useState(
    defaultValues.isLayoutLoading
  );
  const [data, setData] = useState<any[] | undefined>(defaultValues.data);
  // Rows dropped by removeRows since the last full fetch — the server total
  // still counts them, so consumers subtract this to stay honest until a refetch.
  const [locallyRemovedRowsCount, setLocallyRemovedRowsCount] =
    useState<number>(0);
  // Ref, not state: consumers read it after an await, from closures registered
  // before the removal — a re-render would land too late to help them.
  const justRemovedRowIdsRef = useRef<Set<string>>(new Set());
  const [referenceDatasets, setReferenceDatasets] = useState<
    Record<string, Dataset>
  >({});
  const [slaveDatasets, setSlaveDatasets] = useState<Record<string, Dataset>>(
    defaultValues.slaveDatasets
  );
  // FILTER
  const [activeFilters, setActiveFilters] = useState<Record<
    string,
    unknown
  > | null>(defaultValues.activeFilters);
  const [activeSorting, setActiveSorting] = useState<any[]>([]);
  const [filter, setFilter] = useState(defaultValues.filter);
  const [slaveFilters, setSlaveFilters] = useState<Record<string, unknown>>(
    defaultValues.slaveFilters
  );
  //OTHER
  const [cachedQueryId, setCachedQueryId] = useState(
    defaultValues.cachedQueryIdRef
  );
  const [offset, setOffset] = useState(0);
  const [horizontalScroll, setHorizontalScroll] = useState<number>(
    defaultValues.horizontalScroll
  );
  const [columnsWidth, setColumnsWidth] = useState<ColumnsWitdhType>(undefined);
  const [gridLoading, setGridLoading] = useState(true);
  const [gridHeaderHeight, setGridHeaderHeight] = useState(
    defaultValues.gridHeaderHeight
  );
  const [slaveTabIndex, setSlaveTabIndex] = useState(
    defaultValues.slaveTabIndex
  );
  const [prevPeersetFilterId, setPrevPeersetFilterId] = useState<null | string>(
    null
  );
  const [prevPeersetFilterFirst, setPrevPeersetFilterFirst] = useState<{
    mandatory?: string[];
    active?: string[];
  } | null>(null);
  const [peersetFilterFirstProcessed, setPeersetFilterFirstProcessedState] =
    useState(defaultValues.peersetFilterFirstProcessed);
  //REFS
  const cachedQueryIdRef = useRef(cachedQueryId);
  // useFormReturnDetailFetchGuard restores the flag and calls
  // locateAndFetchById in the same pass — the state value is stale there, so
  // the locate guard reads this ref instead.
  const peersetFilterFirstProcessedRef = useRef(
    defaultValues.peersetFilterFirstProcessed
  );
  const lazyLoadingRef = useRef(lazyLoading);
  const disabledLazyLoadingRef = useRef(disabledLazyLoading);
  const offsetRef = useRef(offset);
  const lazyStateRef = useRef(lazyState);
  const activeFiltersRef = useRef(activeFilters);
  const activeSortingRef = useRef(activeSorting);
  const gridLoadingRef = useRef(gridLoading);
  // datasetId of the route we are currently on; distinguishes a genuine peerset switch
  // from a stale `dataset` left behind by a form route (no datasetId).
  const prevDatasetIdRef = useRef<string | undefined>(undefined);

  const { pathname, search, state } = useLocation<GlobalLocationStateType>();
  const history = useHistory();
  const routes = useRoutes();

  const { t } = useTranslate();
  const { fromItemExternalUrl, setDatasetSessionStorageState } =
    useDatasetSessionStorageContext();

  const {
    data: dataset,
    fetch: fetchDataset,
    error: datasetError,
    isLoading: datasetHookLoading,
  } = useDataset(datasetId || '');

  const {
    data: datasetData,
    fetch: fetchData,
    detailedFetch: detailedFetchData,
    error: dataError,
    loadingDatasetId: dataHookLoadingDatasetId,
    cancel: cancelDatasetData,
    reset: resetDatasetDataHook,
    clearError: clearDataError,
  } = useDatasetData();

  const {
    data: datasetFilter,
    fetch: fetchFilter,
    error: filterError,
    isLoading: filterHookLoading,
  } = useFilter();

  const { getActualTokenFromUrl } = useFormTokenChainContext();
  const { getResponseActionHandler } = useResponseActionsContext();

  const {
    state: { token: sessionToken },
  } = useUserState();

  // Suppress (and drop) a row-data poll error that errors under a form opened
  // before the data first loaded — see useFormPollErrorGuard.
  const suppressDataError = useFormPollErrorGuard({
    data,
    datasetId,
    dataError,
    clearDataError,
  });

  // Data a `dataset` control passed to this dataset — opaque to the frontend,
  // exposed to apiMethod templates as the `datasetControl` request property.
  const { datasetControl, datasetControlRef, clearDatasetControl } =
    useDatasetControlData({
      dataset,
      datasetId,
      filter,
      activeFiltersRef,
      setActiveFilters,
      setLazyState,
    });

  // Control data scoped to the dataset a request is built for (guards against
  // a stale ref during cross-dataset transitions).
  const getRequestDatasetControl = (requestDatasetId: string | undefined) => {
    const entry = datasetControlRef.current;
    if (!entry || !requestDatasetId || entry.datasetId !== requestDatasetId) {
      if (entry && requestDatasetId && entry.datasetId !== requestDatasetId) {
        /** @debugFeature datasetControl Logs held dataset control data silently dropped from a request because it belongs to a different dataset (stale ref during transition). */
        logDatasetControl('dataset control data dropped from request', () => ({
          entryDatasetId: entry.datasetId,
          requestDatasetId,
        }));
      }
      return undefined;
    }
    return entry.data;
  };

  // Item `visible` expressions resolved to booleans; unresolved expression items stay hidden
  const visibilityResolvedDataset = useDatasetVisibilityResolve(
    dataset,
    activeFilters
  );

  const apiError = useMemo(() => {
    if (datasetError && datasetError.datasetId === datasetId) {
      return datasetError;
    }

    if (dataError && dataError.datasetId === datasetId && !suppressDataError) {
      return dataError;
    }

    if (filterError && filterError.datasetId === datasetId) {
      return filterError;
    }

    return undefined;
  }, [datasetError, datasetId, dataError, filterError, suppressDataError]);

  /*METHODS*/
  const setPeersetFilterFirstProcessed: Dispatch<SetStateAction<boolean>> =
    useCallback((value) => {
      peersetFilterFirstProcessedRef.current =
        typeof value === 'function'
          ? value(peersetFilterFirstProcessedRef.current)
          : value;
      setPeersetFilterFirstProcessedState(value);
    }, []);

  const fetchDatasetData = async (
    lazy = false,
    queryId?: string,
    activeSorting?: any[],
    activeFilters?: Record<string, unknown>,
    customLimit?: number,
    customOffset?: number,
    // Fills datasetData.totalCount when the server omits the header
    // (customLimit locate responses) — see getResult in useDatasetData.
    fallbackTotalCount?: number
  ) => {
    if (!dataset) {
      return;
    }

    if (!lazy && !queryId?.trim()) {
      setCachedQueryId('');
      cachedQueryIdRef.current = '';
    }

    const limit = customLimit || GRID_DATA_LIMIT;
    let dataOffset = 0;

    if (lazy) {
      dataOffset = offsetRef.current + limit;
    }

    if (customOffset !== undefined) {
      dataOffset = customOffset;
    }

    const resolvedActiveFilters = stripEmptyActiveFilters(
      activeFilters || activeFiltersRef.current || {}
    );
    const resolvedActiveSorting =
      activeSorting || activeSortingRef.current || [];
    const actualToken = getActualTokenFromUrl();

    setDatasetFetchState(
      dataOffset,
      lazy,
      setOffset,
      setDisabledLazyLoading,
      customLimit
    );

    const requestDatasetControl = getRequestDatasetControl(dataset.datasetId);
    const filters = getFilterParamObject(resolvedActiveFilters);

    /** @debugFeature filters Logs the final filter param object of every master dataset request, and the filter keys stripped for empty values. */
    logFilters('request filters built', () => {
      const inputFilters = activeFilters || activeFiltersRef.current || {};
      return {
        datasetId: dataset.datasetId,
        filters,
        strippedEmptyKeys: Object.keys(inputFilters).filter(
          (key) => !(key in resolvedActiveFilters)
        ),
      };
    });
    // Items param must reflect dynamic `visible` state for the filters
    const requestDataset = await resolveDatasetItemsVisibility(
      dataset,
      resolvedActiveFilters
    );
    const requestData: DatasetDataApiRequestData | SubsetDataApiRequestData =
      actualToken?.meta?.requestData?.['master']
        ? getSubsetDataApiRequestData(
            actualToken?.meta?.requestData?.['master'].id,
            requestDataset,
            resolvedActiveSorting,
            sessionToken,
            dataOffset,
            filters,
            undefined,
            // A fresh request omits cachedQueryId; lazy/locate requests reuse it.
            queryId,
            undefined,
            undefined,
            undefined,
            requestDatasetControl
          )
        : getDatasetDataApiRequestData(
            requestDataset,
            resolvedActiveSorting,
            sessionToken,
            dataOffset,
            filters,
            limit,
            // A fresh request omits cachedQueryId; lazy/locate requests reuse it.
            queryId,
            undefined,
            undefined,
            requestDatasetControl
          );

    // String api method request flow
    if (typeof dataset.apiMethod === 'string') {
      const { apiUrl } = await resolveTemplate(
        { apiUrl: dataset.apiMethod },
        requestData
      );

      const paramsString = getDataParamsString(
        resolvedActiveFilters,
        resolvedActiveSorting,
        requestDataset,
        limit,
        dataOffset
      );

      const data = await fetchData(
        `${apiUrl}${paramsString}`,
        dataset,
        lazy,
        queryId,
        undefined,
        undefined,
        undefined,
        fallbackTotalCount
      );
      syncCachedQueryIdRef(data, lazy, cachedQueryIdRef, datasetId);
      return data?.data;
    }

    // Object api method request flow
    return await apiRequest(
      dataset.apiMethod,
      requestData,
      (apiMethod) =>
        getDataFromDatasetRequest(async () => {
          const res = await detailedFetchData(
            apiMethod,
            dataset,
            lazy,
            undefined,
            undefined,
            undefined,
            fallbackTotalCount
          );
          // Before apiRequest runs responseActions — a handler that refetches
          // must not have its fresher id overwritten by this response.
          syncCachedQueryIdRef(res, lazy, cachedQueryIdRef, datasetId);
          return res;
        }),
      getResponseActionHandler
    );
  };

  const reloadDatasetDefinition = async (): Promise<void> => {
    cancelDatasetData();
    setDatasetLoading(true);
    setGridLoading(true);
    setData(undefined);
    resetDatasetDataHook();
    setCachedQueryId('');
    cachedQueryIdRef.current = '';
    await fetchDataset();
  };

  const removeRowsLocally = useCallback(
    (ids: string[]) => {
      const dataKey = dataset?.idItem;
      if (!dataKey) {
        console.warn(
          '[removeRows] dataset has no idItem — cannot remove rows locally'
        );
        return;
      }
      const idSet = new Set(ids.map(String));
      justRemovedRowIdsRef.current = idSet;
      const removedCount = (data ?? []).filter((row) =>
        idSet.has(String(row[dataKey]))
      ).length;
      setLocallyRemovedRowsCount((count) => count + removedCount);
      setData((old) => old?.filter((row) => !idSet.has(String(row[dataKey]))));
    },
    [dataset?.idItem, data]
  );

  // Optimistic single-row merge by idItem — mirrors removeRowsLocally. Used to
  // reflect an external editor's returned row immediately; a refetch reconciles.
  const patchRowLocally = useCallback(
    (id: string, patch: Record<string, unknown>) => {
      const dataKey = dataset?.idItem;
      if (!dataKey) {
        console.warn(
          '[patchRow] dataset has no idItem — cannot patch row locally'
        );
        return;
      }
      if (!patch || Object.keys(patch).length === 0) {
        return;
      }
      setData((old) =>
        old?.map((row) =>
          String(row[dataKey]) === String(id) ? { ...row, ...patch } : row
        )
      );
    },
    [dataset?.idItem]
  );

  const wasRowJustRemoved = useCallback(
    (id: string) => justRemovedRowIdsRef.current.has(String(id)),
    []
  );

  const loadMoreData = useCallback(() => {
    if (
      data?.length === datasetData?.totalCount ||
      datasetData?.totalCount === undefined
    )
      return;

    if (
      !lazyLoadingRef.current &&
      !gridLoadingRef.current &&
      !disabledLazyLoadingRef.current
    ) {
      setLazyLoading(true);
      lazyLoadingRef.current = true;
      fetchDatasetData(true, cachedQueryIdRef.current).then((rows) => {
        // Failed/dropped response never reaches the datasetData effect that
        // clears the flag — release it here or lazy loading deadlocks.
        if (rows === undefined) {
          setLazyLoading(false);
          lazyLoadingRef.current = false;
        }
      });
    }
  }, [
    lazyLoadingRef,
    gridLoadingRef,
    disabledLazyLoadingRef,
    fetchDatasetData,
  ]);

  // Find the row by ID with current filters/sort, then refetch with a high
  // enough limit to include it; the ID-only probe avoids mutating `data`.
  const locateAndFetchById = async (
    itemId: string,
    forcedNewData: boolean,
    mismatchCallback?: () => void,
    cleanup?: () => void,
    refetchOnMiss: boolean = false
  ): Promise<void> => {
    if (
      !dataset?.apiMethod ||
      dataset.datasetId !== datasetId ||
      (dataset.filterFirst &&
        forcedNewData === false &&
        !peersetFilterFirstProcessedRef.current)
    ) {
      /** @debugFeature locate Logs a locate-by-id rejected before the probe (rowNotFound toast), with the gate reasons. */
      logLocate('locate rejected before probe', () => ({
        itemId,
        hasApiMethod: Boolean(dataset?.apiMethod),
        datasetIdMatch: dataset?.datasetId === datasetId,
        filterFirstUnprocessed: Boolean(
          dataset?.filterFirst &&
          forcedNewData === false &&
          !peersetFilterFirstProcessedRef.current
        ),
      }));

      toast(t('toasts.rowNotFound'));
      cleanup?.();
      return;
    }
    // All filtered rows are already loaded; if not in `data`, skip probe and
    // surface "not found" immediately.
    if (
      data &&
      datasetData?.totalCount !== undefined &&
      data.length >= datasetData.totalCount &&
      forcedNewData === false
    ) {
      /** @debugFeature locate Logs a locate-by-id resolved without a probe: all filtered rows are loaded and the id is not among them (rowNotFound toast). */
      logLocate('locate skipped, row not in fully loaded data', () => ({
        itemId,
        loadedRows: data.length,
        totalCount: datasetData.totalCount,
      }));

      toast(t('toasts.rowNotFound'));
      cleanup?.();
      return;
    }

    if (forcedNewData) {
      setCachedQueryId('');
      cachedQueryIdRef.current = '';
    }

    // Lock cachedQueryId once so probe and customLimit fetch use the same
    // server cache; this keeps row ordering and probe index alignment stable.
    const lockedCachedQueryId = cachedQueryIdRef.current;

    const { idItem, resolvedFilters, resolvedSorting, requestData } =
      buildLocateRequestData(
        dataset,
        activeFilters,
        activeSorting,
        sessionToken,
        undefined,
        lockedCachedQueryId || null,
        getRequestDatasetControl(dataset.datasetId)
      );

    const probe = await fetchRowIds({
      dataset,
      requestData,
      resolvedFilters,
      resolvedSorting,
      idItem,
      sessionToken,
    });

    if (!probe) {
      /** @debugFeature locate Logs a failed id-only probe (rowNotFound toast). */
      logLocate('locate probe failed', () => ({ itemId }));

      toast(t('toasts.rowNotFound'));
      cleanup?.();
      return;
    }

    // Probe cache differs from the locked initial cache, so any computed index
    // would target stale data.
    if (
      lockedCachedQueryId &&
      probe.cachedQueryId &&
      probe.cachedQueryId !== lockedCachedQueryId
    ) {
      /** @debugFeature locate Logs a probe whose server cache differs from the locked one — a computed index would target stale data (newDataLoaded toast). */
      logLocate('locate cache mismatch', () => ({
        itemId,
        lockedCachedQueryId,
        probeCachedQueryId: probe.cachedQueryId,
      }));

      toast(t('toasts.newDataLoaded'));
      mismatchCallback?.();
      cleanup?.();
      return;
    }

    const index = probe.ids.findIndex(
      (row) => String(row?.[idItem]) === itemId
    );
    if (index === -1) {
      /** @debugFeature locate Logs an id missing from the probe result — the row left the current filter or was deleted (rowNotFound toast, optional refetch). */
      logLocate('locate id not in probe result', () => ({
        itemId,
        probeRowCount: probe.ids.length,
        refetchOnMiss,
      }));

      toast(t('toasts.rowNotFound'));
      // Grid holds pre-probe data — refetch so a row that left the filter drops out
      if (refetchOnMiss) {
        await fetchDatasetData(
          false,
          lockedCachedQueryId,
          resolvedSorting,
          resolvedFilters
        );
      }
      cleanup?.();
      return;
    }

    // No clamp to a total: the server omits X-CACHEDQUERY-TOTALCNT on the
    // id-probe and datasetData.totalCount can be stale (an external
    // single-record fetch leaves it at 1, which would shrink the window to one
    // row). Rounding the probe index up to the next page chunk already
    // guarantees the row is inside the fetched payload; over-requesting a few
    // rows past the end is harmless (the server returns only what exists).
    const locateLimit = computeCustomLocateLimit(index);

    /** @debugFeature locate Logs a located row: its probe index and the window limit the follow-up fetch uses. */
    logLocate('row located, fetching window', () => ({
      itemId,
      index,
      locateLimit,
      probeRowCount: probe.ids.length,
    }));

    await fetchDatasetData(
      false,
      lockedCachedQueryId,
      resolvedSorting,
      resolvedFilters,
      locateLimit
    );

    // Server omits X-CACHEDQUERY-TOTALCNT on the locate response (esp. with
    // empty cachedQueryId), so the datasetData effect's reachedEnd fallback
    // wrongly treats a full payload (>= GRID_DATA_LIMIT) as partial.
    // probe.ids is the authoritative row count — flip the flag directly.
    if (probe.ids.length <= locateLimit) {
      setDisabledLazyLoading(true);
      disabledLazyLoadingRef.current = true;
    }
  };

  // Multi-id sibling of locateAndFetchById: one id-only probe finds where
  // every wanted row sits, one window fetch loads data up to the farthest one.
  // onLocated fires with that row's id BEFORE the fetch so the caller can arm
  // the grid scroll. Any failure = silent no-op, plain page-1 data stays.
  const locateAndFetchFarthestByIds = async (
    itemIds: string[],
    onLocated?: (farthestId: string) => void
  ): Promise<void> => {
    if (itemIds.length === 0) return;
    if (!dataset?.apiMethod || dataset.datasetId !== datasetId) return;

    // Lock cachedQueryId once so probe and customLimit fetch use the same
    // server cache; this keeps row ordering and probe index alignment stable.
    const lockedCachedQueryId = cachedQueryIdRef.current;

    const probeResult = await probeRowIndexesByIds({
      dataset,
      activeFilters,
      activeSorting,
      sessionToken,
      lockedCachedQueryId,
      itemIds,
      datasetControl: getRequestDatasetControl(dataset.datasetId),
    });
    if (!probeResult) {
      /** @debugFeature locate Logs a failed multi-id probe — a silent no-op, plain page-1 data stays. */
      logLocate('multi-id locate probe failed, keeping page-1 data', () => ({
        itemIds,
      }));
      return;
    }

    onLocated?.(probeResult.farthestId);

    const locateLimit = computeCustomLocateLimit(probeResult.farthestIndex);

    /** @debugFeature locate Logs a multi-id locate: the farthest row and the window limit the follow-up fetch uses. */
    logLocate('multi-id rows located, fetching window', () => ({
      itemIds,
      farthestId: probeResult.farthestId,
      farthestIndex: probeResult.farthestIndex,
      locateLimit,
      totalRowCount: probeResult.totalRowCount,
    }));
    // totalRowCount backfills the missing totalCount header so lazy loading
    // keeps working past the fetched window.
    await fetchDatasetData(
      false,
      lockedCachedQueryId,
      probeResult.resolvedSorting,
      probeResult.resolvedFilters,
      locateLimit,
      undefined,
      probeResult.totalRowCount
    );

    if (probeResult.totalRowCount <= locateLimit) {
      setDisabledLazyLoading(true);
      disabledLazyLoadingRef.current = true;
    }
  };

  const resetDatasetSetup = useCallback(() => {
    // Leaving the current dataset: drop any long-running in-flight data request
    // so its late response can't flip loading/data/errors on the new dataset.
    cancelDatasetData();
    justRemovedRowIdsRef.current = new Set();

    // Keep active filters between peersets with the same filterId
    const isPeerset = isPeersetSwitch(
      dataset,
      datasetId,
      prevDatasetIdRef.current
    );

    // Arriving with control data always starts from clean user filters —
    // even a peerset carryover would violate the control contract.
    const hasDatasetControlParam = parseDatasetControlData(search) !== null;

    if (isPeerset && dataset?.filterId && !hasDatasetControlParam) {
      setPrevPeersetFilterId(dataset.filterId);
      setPrevPeersetFilterFirst(dataset.filterFirst ?? null);
    } else {
      setActiveFilters(null);
      setPrevPeersetFilterId(null);
      setPrevPeersetFilterFirst(null);
      setPeersetFilterFirstProcessed(false);
      setFilter(null);
    }
    setGridLoading(true);
    setData(undefined);
    // Fixes data not fetching if you switch to dataset B before dataset A definition is loaded
    resetDatasetDataHook();
    setOffset(0);
    setCachedQueryId('');
    setDisabledLazyLoading(false);
    setLazyLoading(false);
    setHorizontalScroll(0);
    setLazyState(DEFAULT_LAZY_STATE);
    setSlaveLazyStates({});
    setColumnsWidth(undefined);
    setGridHeaderHeight(0);
    setReferenceDatasets({});
    setSlaveDatasets({});
    setSlaveFilters({});
    setSlaveTabIndex(defaultValues.slaveTabIndex);
    setActiveSorting([]);

    if (datasetId) {
      fetchDataset().then((dataset) => {
        /**
         * First fetched dataset is set into the session after the row detail is displayed in DatasetDetail
         */
        if (dataset && !fromItemExternalUrl) {
          setDatasetSessionStorageState({
            lastLoadedDataset: dataset?.datasetId,
          });
        }
      });

      const { itemId, subsetId } = getSubsetAndItemParams(pathname);

      /**
       * Redirects to DatasetDetail when the app cold-starts on an /item/:itemId URL
       * via external link or new tab. Page reload will be ignored.
       */
      if (fromItemExternalUrl && itemId && !isFromPageReload()) {
        const target = subsetId
          ? routes.datasetDetailSubset(datasetId, itemId, subsetId)
          : routes.datasetDetail(datasetId, itemId);
        // Mark the detail as external-origin so a control opening a form from
        // here stamps the token (the list holds only this single record).
        history.replace(target, { itemId, enteredFromExternalUrl: true });
      }
    } else {
      // Standalone form route (/form/:formId/add) has no dataset to fetch.
      setDatasetLoading(false);
    }
  }, [dataset, datasetId]);

  /*EFFECTS*/
  // Leaving the manager entirely: cancel any in-flight data request so its late
  // response is dropped instead of hitting an unmounted provider.
  useEffect(() => {
    return () => {
      cancelDatasetData();
    };
  }, []);

  useEffect(() => {
    setDatasetLoading(true);
    resetDatasetSetup();
    prevDatasetIdRef.current = datasetId;
  }, [datasetId]);

  // On dataset change effect
  useEffect(() => {
    if (dataset && dataset.datasetId === datasetId) {
      const multiSortMeta = getMultisortFromDataset(dataset);
      const newActiveSorting = multiSortMeta || [];
      setActiveSorting(newActiveSorting);

      if (multiSortMeta || dataset.filter) {
        setLazyState((oldLazyState) => ({
          ...oldLazyState,
          multiSortMeta: multiSortMeta || null,
          filters: null,
        }));
      }
      const { reuseFiltersForSamePeerset, resetActiveFilters } =
        getPeersetFilterTransition({
          prevPeersetFilterId,
          prevPeersetFilterFirst,
          filterId: dataset.filterId,
          filterFirst: dataset.filterFirst,
        });
      // Reset filters when the filter differs from the previous peerset, or when the
      // filterFirst modal reopens so it shows filterFirst defaults, not carried-over values
      if (resetActiveFilters) {
        setActiveFilters(null);
        activeFiltersRef.current = null;
        setPeersetFilterFirstProcessed(false);
        setFilter(null);
      }
      if (
        dataset.filterId &&
        (dataset.filterFirst ||
          pathname.endsWith('/filter') ||
          pathname.endsWith('/reference')) &&
        resetActiveFilters
      ) {
        fetchFilter(dataset.filterId, dataset.datasetId);
      }
      setLazyState((oldLazyState) =>
        // get lazyState filters from active filters if they are passed from previous peerset
        // otherwise generates default lazyState filters
        updateLazyStateFromActiveFilters(
          oldLazyState,
          activeFiltersRef.current,
          dataset,
          filter
        )
      );
      // New reference re-triggers the data fetch effect since values carry over unchanged
      if (reuseFiltersForSamePeerset && activeFiltersRef.current) {
        const refreshed = { ...activeFiltersRef.current };
        activeFiltersRef.current = refreshed;
        setActiveFilters(refreshed);
      }
      if (dataset.subsets) {
        const newSlaveLazyStates = {};
        dataset.subsets.forEach(({ datasetId }) => {
          newSlaveLazyStates[datasetId] = DEFAULT_LAZY_STATE;
        });
        setSlaveLazyStates(newSlaveLazyStates);
      }
      setDatasetLoading(false);
    }
  }, [dataset]);

  useQuickBookmarkFilterRestore({
    dataset,
    datasetId,
    filter,
    activeFiltersRef,
    setActiveFilters,
    setLazyState,
    setPeersetFilterFirstProcessed,
    activeSorting,
    cancelDatasetData,
    fetchDatasetData,
  });

  // On datasetData change effect
  useEffect(() => {
    // Prevents bugs with data requested before page change
    if (datasetData && datasetData.datasetId === datasetId && dataset) {
      let newData: any[] = [];
      // Disable lazy when we've fetched all rows. With customLimit (locate-by-id)
      // the chunk size can differ from GRID_DATA_LIMIT, so prefer totalCount
      // when the server reports it.
      const fetchedSoFar = datasetData.lazy
        ? offsetRef.current + datasetData.data.length
        : datasetData.data.length;
      const reachedEnd =
        datasetData.totalCount !== undefined
          ? fetchedSoFar >= datasetData.totalCount
          : datasetData.data.length < GRID_DATA_LIMIT;
      if (reachedEnd) {
        setDisabledLazyLoading(true);
        disabledLazyLoadingRef.current = true;
      }
      // add unique row id to datasets that have no unique id
      const resolveRows = (startIndex: number): any[] =>
        dataset.idItem
          ? datasetData.data
          : withSyntheticRowIds(datasetData.data, startIndex);
      newData = resolveRows(0);
      const newColumnsWidth = getColumnsWidth(
        dataset,
        newData,
        columnsWidth,
        lazyState
      );
      setColumnsWidth(newColumnsWidth);
      if (datasetData.lazy) {
        setLazyLoading(false);
        // Stale lazy response after sort/filter reset — discard to avoid spurious toast.
        if (
          cachedQueryIdRef.current === '' &&
          datasetData.cachedQueryId !== undefined
        ) {
          return;
        }
        if (
          datasetData.cachedQueryId === cachedQueryIdRef.current ||
          // Use lazyloading withoud server cache id if server did not return X-CACHEDQUERY-ID
          datasetData.cachedQueryId === undefined
        ) {
          setData((oldData) => {
            if (!oldData) {
              return newData;
            }
            return [...oldData, ...resolveRows(oldData.length)];
          });
        } else {
          // Reset data if X-CACHEDQUERY-ID changed
          toast(t('toasts.newDataLoaded'));
          setOffset(0);
          setData(newData);
          setLocallyRemovedRowsCount(0);
        }
      } else {
        setGridLoading(false);
        setData(newData);
        setLocallyRemovedRowsCount(0);
      }
      if (datasetData.cachedQueryId !== undefined) {
        setCachedQueryId(datasetData.cachedQueryId);
        // Sync ref synchronously so an immediate follow-up lazy load reads
        // the fresh cachedQueryId instead of the stale value (otherwise the
        // server returns a new query id and triggers the newDataLoaded toast).
        cachedQueryIdRef.current = datasetData.cachedQueryId;
      }
      const sameFilterAsPreviousPeerset =
        prevPeersetFilterId && dataset.filterId === prevPeersetFilterId;
      if (
        dataset.filterId &&
        !dataset.filterFirst &&
        !filter &&
        !filterHookLoading &&
        !sameFilterAsPreviousPeerset
      ) {
        fetchFilter(dataset.filterId, dataset.datasetId);
      }
      setPrevPeersetFilterId(null);
      setPrevPeersetFilterFirst(null);
    }
  }, [datasetData]);

  // On lazyState change effect
  useEffect(() => {
    lazyStateRef.current = lazyState;

    if (dataset && data && columnsWidth) {
      const newColumnsWidth = getColumnsWidth(
        dataset,
        data,
        columnsWidth,
        lazyState,
        true
      );
      setColumnsWidth(newColumnsWidth);
    }
    // Update active filters on columns filter change
    if (dataset) {
      if (
        lazyState.filters !== null &&
        !state?.fromFilterFirstInit &&
        // Prevents default activeFilters resetting when opening filterFirst dataset
        (activeFilters !== null || !dataset.filterFirst)
      ) {
        setActiveFilters((oldActiveFilters) => {
          const newState = updateActiveFiltersFromLazyState(
            oldActiveFilters,
            lazyState,
            dataset
          );
          activeFiltersRef.current = newState;
          return newState;
        });
      }
      setActiveSorting((oldActiveSorting) => {
        const newState = updateActiveSortingFromLazyState(
          oldActiveSorting,
          lazyState
        );
        activeSortingRef.current = newState;
        return newState;
      });
    }
  }, [lazyState]);

  // On dataLoading change effect
  useEffect(() => {
    // data===undefined means sort/filter reset cleared it and a refetch is still in-flight.
    // Don't hide the spinner — setGridLoading(false) is called explicitly when data arrives.
    if (
      dataHookLoadingDatasetId === undefined &&
      data === undefined &&
      !apiError
    ) {
      return;
    }
    setGridLoading(dataHookLoadingDatasetId !== undefined);
  }, [dataHookLoadingDatasetId]);

  // Clear stale data + cache id BEFORE child effects observe the change.
  // useLayoutEffect runs after DOM mutation but before paint, and parent
  // layout effects run after child layout effects but before any useEffect.
  // Ensures DatasetList's useEffect [data] sees data=undefined (early return
  // in handleExternalUrl) instead of running locate against the previous
  // filter's cachedQueryIdRef.
  useLayoutEffect(() => {
    if (
      !datasetLoading &&
      dataset?.apiMethod &&
      dataset?.datasetId === datasetId &&
      activeFilters !== null &&
      !pathname.endsWith('/filter')
    ) {
      setData(undefined);
      setCachedQueryId('');
      cachedQueryIdRef.current = '';
    }
  }, [activeFilters, activeSorting]);

  // Fetch datasetdata trigger effect
  useEffect(() => {
    if (
      // Fetch correct data if user changes sorting while lazyloading is fetching data
      (dataHookLoadingDatasetId !== datasetId || lazyLoading) &&
      !datasetLoading &&
      dataset?.apiMethod &&
      dataset?.datasetId === datasetId &&
      activeFilters !== null &&
      // Skip initial fetch for non-filterFirst datasets: DatasetList owns that case.
      // Context only re-fetches when data exists (filter/sort change) or dataset is filterFirst.
      (data !== undefined || dataset?.filterFirst) &&
      !pathname.endsWith('/filter') &&
      !fromItemExternalUrl &&
      // External single-record display (fromItemExternalUrl flips false
      // mid-display): a filtered list fetch would clobber the displayed row.
      !state?.enteredFromExternalUrl
    ) {
      /** @debugFeature filters Logs a filter/sort change that triggered a dataset fetch, with the filters used. */
      logFilters('filter change triggered fetch', () => ({
        datasetId,
        activeFilters,
        activeSorting,
      }));

      setGridLoading(true);
      setLazyLoading(false);
      fetchDatasetData(
        undefined,
        undefined,
        activeSorting.length > 0 ? activeSorting : undefined,
        activeFilters
      );
    } else {
      /** @debugFeature filters Logs a filter/sort change that did NOT trigger a fetch, with the gate conditions that suppressed it. */
      logFilters('filter change did not trigger fetch', () => ({
        datasetId,
        loadingMismatch: dataHookLoadingDatasetId !== datasetId || lazyLoading,
        datasetLoading,
        hasApiMethod: Boolean(dataset?.apiMethod),
        datasetIdMatch: dataset?.datasetId === datasetId,
        activeFiltersNull: activeFilters === null,
        hasDataOrFilterFirst:
          data !== undefined || Boolean(dataset?.filterFirst),
        onFilterRoute: pathname.endsWith('/filter'),
        fromItemExternalUrl,
        enteredFromExternalUrl: Boolean(state?.enteredFromExternalUrl),
      }));
    }
  }, [activeFilters, activeSorting]);

  // On datasetFilter change effect
  useEffect(() => {
    if (datasetFilter && datasetFilter.datasetId === datasetId) {
      // Defaults applied to activeFilters for filterFirst columns; stays {} for
      // non-filterFirst datasets. Hoisted so the lazyState updater below can seed
      // the same values into lazyState (keeps activeFilters and lazyState in sync).
      const newActiveFilters: Record<string, any> = {};
      if (
        datasetFilter.filter?.items &&
        (dataset?.filterFirst?.active || dataset?.filterFirst?.mandatory) &&
        // filterFirst already passed (filter submit, persisted-filter hydrate,
        // or the detail-reload restore) — seeding would overwrite the user's
        // filters with defaults, or desync activeFilters from a locate window.
        !peersetFilterFirstProcessedRef.current &&
        // External single-record display: the user never picked the filters,
        // so leaving the display (close, form return) must land on an
        // unfiltered list — do not seed the defaults under it.
        !state?.enteredFromExternalUrl
      ) {
        if (dataset.filterFirst.mandatory) {
          dataset.filterFirst.mandatory.forEach((filter) => {
            const foundFilter = datasetFilter.filter.items.find(
              ({ name }) => name === filter
            );
            if (foundFilter) {
              newActiveFilters[filter] = getDefaultFilterFromApiResponse(
                foundFilter,
                getColumnByFilterParam(filter, dataset)?.type
              );
            }
          });
        }
        if (dataset.filterFirst.active) {
          dataset.filterFirst.active.forEach((filter) => {
            if (!newActiveFilters[filter]) {
              const foundFilter = datasetFilter.filter.items.find(
                ({ name }) => name === filter
              );
              if (foundFilter) {
                newActiveFilters[filter] = getDefaultFilterFromApiResponse(
                  foundFilter,
                  getColumnByFilterParam(filter, dataset)?.type
                );
              }
            }
          });
        }
        /** @debugFeature filters Logs the filterFirst defaults seeded into activeFilters, and the filterFirst columns silently dropped because the filter definition does not contain them. */
        logFilters('filterFirst defaults seeded', () => {
          const requested = [
            ...(dataset.filterFirst?.mandatory ?? []),
            ...(dataset.filterFirst?.active ?? []),
          ];
          return {
            datasetId,
            seeded: newActiveFilters,
            droppedUnknownColumns: requested.filter(
              (name) => !(name in newActiveFilters)
            ),
          };
        });

        setActiveFilters(newActiveFilters);
      }

      if (dataset?.items && datasetFilter.filter.items) {
        // Update default matchMode value for multiCheckbox and reference filters
        setLazyState((oldLazyState) => {
          const newFilters = {
            ...(oldLazyState.filters as Record<string, any>),
          };
          dataset.items?.forEach((column) => {
            if (column.filterParam && newFilters[column.name]) {
              const filterItem = datasetFilter.filter.items.find(
                ({ name }) => name === column.filterParam
              );
              // Keep existing matchMode if value is set from previous peerset
              if (isEmptyValue(newFilters[column.name].constraints[0].value)) {
                const appliedDefault = newActiveFilters[column.filterParam];
                if (appliedDefault) {
                  // Mirror the filterFirst default that was just written to
                  // activeFilters into lazyState, so the column-filter inputs
                  // render it AND updateActiveFiltersFromLazyState rebuilds an
                  // identical activeFilters (no second, unfiltered fetch).
                  newFilters[column.name].constraints[0].matchMode =
                    Object.keys(appliedDefault)[0];
                  newFilters[column.name].constraints[0].value =
                    Object.values(appliedDefault)[0];
                } else if (
                  filterItem?.control?.type === 'multiCheckbox' ||
                  filterItem?.control?.type === 'reference'
                ) {
                  const newMatchMode = getSupportedDefaultMatchMode(
                    filterItem,
                    column.type
                  );
                  newFilters[column.name].constraints[0].matchMode =
                    newMatchMode;
                }
              }
            }
          });
          return { ...oldLazyState, filters: newFilters };
        });
      }
      setFilter(datasetFilter.filter);
    }
  }, [datasetFilter]);

  /*RESSETTERS ??*/
  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  useEffect(() => {
    activeSortingRef.current = activeSorting;
  }, [activeSorting]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    lazyLoadingRef.current = lazyLoading;
  }, [lazyLoading]);

  useEffect(() => {
    gridLoadingRef.current = gridLoading;
  }, [gridLoading]);

  useEffect(() => {
    cachedQueryIdRef.current = cachedQueryId;
  }, [cachedQueryId]);

  useEffect(() => {
    disabledLazyLoadingRef.current = disabledLazyLoading;
  }, [disabledLazyLoading]);

  /*CONTEXT VALUE*/
  const value = useMemo(
    () => ({
      isProvided: true,
      activeSorting,
      activeFilters,
      apiError,
      cachedQueryIdRef: cachedQueryIdRef.current,
      columnsWidth,
      data,
      locallyRemovedRowsCount,
      datasetId,
      subsetId,
      dataset: visibilityResolvedDataset,
      datasetData,
      disabledLazyLoading,
      filter,
      gridHeaderHeight,
      horizontalScroll,
      lazyState,
      peersetFilterFirstProcessed,
      isLayoutLoading: datasetId ? datasetLoading || !dataset : datasetLoading,
      isGridLoading: gridLoading && !lazyLoading,
      isDatasetHookLoading: datasetHookLoading,
      dataHookLoadingDatasetId,
      referenceDatasets,
      slaveLazyStates,
      slaveDatasets,
      slaveFilters,
      slaveTabIndex,
      datasetControl,
      clearDatasetControl,
      setDatasetId,
      fetchDatasetData,
      reloadDatasetDefinition,
      locateAndFetchById,
      locateAndFetchFarthestByIds,
      loadMoreData,
      removeRowsLocally,
      patchRowLocally,
      wasRowJustRemoved,
      setLazyState,
      setGridHeaderHeight,
      setReferenceDatasets,
      setHorizontalScroll,
      setSlaveDatasets,
      setActiveFilters,
      setSlaveLazyStates,
      setSlaveFilters,
      setSlaveTabIndex,
      setSubsetId,
      setPeersetFilterFirstProcessed,
      resetDatasetSetup,
    }),
    [
      activeSorting,
      activeFilters,
      apiError,
      cachedQueryIdRef,
      columnsWidth,
      data,
      locallyRemovedRowsCount,
      datasetId,
      subsetId,
      dataset,
      visibilityResolvedDataset,
      datasetData,
      datasetLoading,
      disabledLazyLoading,
      filter,
      filterHookLoading,
      datasetHookLoading,
      dataHookLoadingDatasetId,
      gridLoading,
      gridHeaderHeight,
      horizontalScroll,
      lazyState,
      lazyLoading,
      referenceDatasets,
      slaveLazyStates,
      slaveDatasets,
      slaveFilters,
      slaveTabIndex,
      datasetControl,
      clearDatasetControl,
      setDatasetId,
      fetchDatasetData,
      reloadDatasetDefinition,
      locateAndFetchById,
      locateAndFetchFarthestByIds,
      loadMoreData,
      removeRowsLocally,
      patchRowLocally,
      wasRowJustRemoved,
      setLazyState,
      setGridHeaderHeight,
      setReferenceDatasets,
      setHorizontalScroll,
      setSlaveDatasets,
      setActiveFilters,
      setSlaveLazyStates,
      setSlaveFilters,
      setSlaveTabIndex,
      setSubsetId,
      setPeersetFilterFirstProcessed,
      resetDatasetSetup,
    ]
  );

  return (
    <DatasetContext.Provider value={value}>{children}</DatasetContext.Provider>
  );
};

export default DatasetContextProvider;
